import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageString } from '@myriadgenetics/mgh-types';
import { Button as RACButton, ListBox, ListBoxItem, Popover, Select, SelectValue } from 'react-aria-components';
import arrowDown from '../../assets/arrow-down.svg';
import Button from '../../elements/button';
import Toggle from '../../elements/toggle';
import { Text2 } from '../../elements/typography';
import Logger from '../../helpers/logger';
import { languageOptions } from './constants';

import './index.scss';
import { LSDropdownProps } from './types';

export enum SelectionType {
  Buttons = 'buttons',
  Toggle = 'toggle',
  Dropdown = 'dropdown',
}

interface Props {
  type: SelectionType;
  size?: 'small' | 'large';
}

function LanguageSelect({ type, size }: Props) {
  const { i18n } = useTranslation();
  const isSpanish: boolean = React.useMemo(() => i18n.language === 'es', [i18n.language]);

  const RenderComponent = React.useMemo(() => {
    const changeLanguage = (lng: LanguageString) => {
      i18n.changeLanguage(lng);
    };

    switch (type) {
      case SelectionType.Buttons:
        return <LanguageSelectButtons changeLanguage={changeLanguage} />;
      case SelectionType.Dropdown:
        return <LanguageSelectDropDown changeLanguage={changeLanguage} size={size} />;
      default:
        return <LanguageSelectToggle changeLanguage={changeLanguage} isSpanish={isSpanish} />;
    }
  }, [type, i18n, isSpanish, size]);

  return <div className="language-select">{RenderComponent}</div>;
}

LanguageSelect.defaultProps = { type: SelectionType.Buttons };

interface LSButtonProps {
  changeLanguage: (lang: LanguageString) => void;
}

function LanguageSelectButtons({ changeLanguage }: LSButtonProps) {
  const { t } = useTranslation();

  const handleOnClick = (language: LanguageString) => {
    Logger.logEvent('Selected Language', {
      language: language,
    });
    changeLanguage(language);
  };

  return (
    <div className="language-select-buttons">
      <div className="language-select-buttons__option">
        <Button onClick={() => handleOnClick(LanguageString.English)}>{t('language')}</Button>
      </div>
      <div className="language-select-buttons__option">
        <Button onClick={() => handleOnClick(LanguageString.Spanish)}>{t('languageEs')}</Button>
      </div>
    </div>
  );
}

interface LSToggleProps {
  changeLanguage: (lang: LanguageString) => void;
  isSpanish: boolean;
}

function LanguageSelectToggle({ changeLanguage, isSpanish }: LSToggleProps) {
  const toggleTo = React.useMemo(() => (isSpanish ? LanguageString.English : LanguageString.Spanish), [isSpanish]);

  return (
    <div className="language-select-toggle">
      <Text2 tag="span">English</Text2>{' '}
      <div className="language-select-toggle__toggle">
        <Toggle
          round={false}
          checked={isSpanish}
          size="small"
          onClick={() => {
            Logger.logEvent('Toggled Language', { toggleTo });
            changeLanguage(toggleTo);
          }}
        />
      </div>{' '}
      <Text2 tag="span">Español</Text2>
    </div>
  );
}

function LanguageSelectDropDown({ changeLanguage, size = 'small' }: LSDropdownProps) {
  const { i18n, t } = useTranslation();
  const getLanguage = (language: string) => (language.startsWith('es') ? LanguageString.Spanish : LanguageString.English);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // The RAC Popover on the RAC Select mispositions itself when resizing. Closing the Select solves the problem.
  const resize = () => {
    setIsSelectOpen(false);
  };

  useEffect(() => {
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    resize();
  }, []);

  const handleChangeLanguage = useCallback(
    (key: any) => {
      Logger.logEvent('Selected Language', {
        language: key,
      });
      changeLanguage(key);
      handleOpenChange(false);
    },
    [changeLanguage],
  );

  const handleOpenChange = (isOpen: boolean) => {
    setTimeout(
      () => {
        setIsSelectOpen(isOpen);
      },
      isOpen ? 0 : 100,
    );
  };

  return (
    <>
      <Select
        defaultSelectedKey={getLanguage(i18n.language)}
        data-testid="language-select"
        onOpenChange={(isOpen: boolean) => handleOpenChange(isOpen)}
        onSelectionChange={(e: any) => handleChangeLanguage(e)}
        isOpen={isSelectOpen}
      >
        <RACButton>
          <SelectValue className={`react-aria-SelectValue ${size === 'large' ? 'heading-small--regular' : 'text-small--regular'}`} />
          <span aria-label="selected language" aria-hidden="true">
            <img className="language-select__icon" src={arrowDown} alt="arrow down" />
          </span>
        </RACButton>
        <Popover className="react-aria-Popover language-select__popover">
          <ListBox>
            {languageOptions.map((language, i) => (
              <ListBoxItem className={`react-aria-ListBoxItem ${size === 'large' ? 'text-medium--medium' : 'text-small--medium'}`} key={`${language.value}`} id={`${language.value}`} textValue={language.label}>
                {t(language.label)}
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </Select>
    </>
  );
}

export default LanguageSelect;
